import { Box, Theme, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useEffect } from "react";
import { useLocation } from "react-router";

import { useCopyLinkToClipboard } from "../../hooks/useCopyLinkToClipboard";
import { usePublicMembershipOffer } from "../../hooks/usePublicMembershipOffer";
import { useToast } from "../classroom/useToast";

export default function FirstMonthFreeBanner() {
  const { t } = useTranslation(
    "client\\components\\banners\\FirstMonthFreeBanner",
  );
  const { wasCopied, onCopy } = useCopyLinkToClipboard();
  const showToast = useToast();
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { couponCode: membershipCouponCode, isTwentyDollarOfferEnabled } =
    usePublicMembershipOffer();

  // Track if banner view has been tracked for this mount to prevent duplicate tracking
  const hasTrackedViewRef = React.useRef<boolean>(false);

  useEffect(() => {
    if (!hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      trackEvent("first_month_free_banner_view", {
        is_mobile: isMobile,
      });

      // Debug tracking: fire additional event when banner renders on the subscribe page
      if (location.pathname.includes("/membership/subscribe/")) {
        trackEvent("debug_first_month_free_banner_on_subscribe_page", {
          is_mobile: isMobile,
          pathname: location.pathname,
        });
      }
    }
  }, [trackEvent, isMobile, location.pathname]);

  const handleBannerClick = () => {
    trackEvent("first_month_free_banner_click", {
      is_mobile: isMobile,
    });
  };

  const copyPromoCodeToClipboard = async () => {
    await onCopy(membershipCouponCode);
    showToast(t("Copied!"), "success");
  };

  const PromoBanner = () => {
    return (
      <>
        <Typography component="span">
          🌱&nbsp;
          {isTwentyDollarOfferEnabled ? (
            <Trans t={t}>Get $20 off your first month. Use code</Trans>
          ) : (
            <Trans t={t}>Get ready with a free trial. Use code</Trans>
          )}
          &nbsp;
        </Typography>
        <Typography
          component="span"
          role="button"
          tabIndex={0}
          aria-label={
            wasCopied ? t("Copied!") : t("Copy promo code to clipboard")
          }
          onClick={copyPromoCodeToClipboard}
          sx={{
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {membershipCouponCode}
        </Typography>
        .
        <Typography
          sx={(theme: Theme) => ({
            [theme.breakpoints.down("md")]: {
              display: "none",
            },
          })}
        >
          &nbsp;
          <Trans t={t}>Limited time.</Trans>
        </Typography>
      </>
    );
  };

  return (
    <Box
      data-testid="first-month-free-banner"
      sx={(theme: Theme) => ({
        display: "flex",
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary[600],
        minHeight: "50px",
        cursor: "pointer",
        padding: "0 1em",
      })}
      onClick={handleBannerClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1440px",
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              <PromoBanner />
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
